import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Mail, Lock, User as UserIcon, Eye, EyeOff } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { HERO_IMAGES } from "../mock";

export default function Auth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  /**
   * Where to go once signed in. Only same-site paths are honoured: `next` comes
   * from the URL, so accepting an absolute one would turn every login link into
   * an open redirect.
   */
  const nextPath = (() => {
    const raw = params.get("next") || "";
    return raw.startsWith("/") && !raw.startsWith("//") ? raw : null;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    setBusy(true);
    const res = isLogin
      ? await login(form.email, form.password)
      : await register(form.name, form.email, form.password);
    setBusy(false);
    if (!res.ok) {
      // The API says what actually went wrong — a short password, a malformed
      // address, a duplicate. Showing "Email already registered" for all of them
      // sent people off to a sign-in that could not work.
      setError(res.error);
      return;
    }
    navigate(nextPath || (res.user.role === "admin" ? "/admin" : "/"));
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      {/* form side */}
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-float-up">
          <Logo size="md" />

          <h1 className="font-display text-3xl font-semibold tracking-tight">
            {isLogin ? t("welcome_back") : t("create_account")}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            {isLogin
              ? "Pick up right where you left off."
              : "Join and start listening today."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {!isLogin && (
              <div className="relative">
                <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("full_name")}
                  className="w-full h-12 pl-11 pr-4 rounded-xl bg-secondary border border-border outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t("email")}
                className="w-full h-12 pl-11 pr-4 rounded-xl bg-secondary border border-border outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                required
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={t("password")}
                className="w-full h-12 pl-11 pr-11 rounded-xl bg-secondary border border-border outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {isLogin && (
              <div className="flex justify-end -mt-1">
                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-brand-400 transition-colors">
                  Forgot your password?
                </Link>
              </div>
            )}

            {error && <p className="text-sm text-red-400">{error}</p>}

            <Button
              type="submit"
              disabled={busy}
              className="w-full h-12 bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-xl text-base disabled:opacity-70"
            >
              {busy
                ? (isLogin ? "Signing in…" : "Creating your account…")
                : (isLogin ? t("login_btn") : t("register_btn"))}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            {isLogin ? t("no_account") : t("have_account")}{" "}
            <Link to={isLogin ? "/register" : "/login"} className="text-brand-400 font-medium hover:text-brand-300">
              {isLogin ? t("signup") : t("signin")}
            </Link>
          </div>

        </div>
      </div>

      {/* visual side */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO_IMAGES[0]} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute bottom-14 left-12 right-12">
          <h2 className="font-display text-4xl font-semibold leading-tight">
            {"Let the story find you."}
          </h2>
          <p className="text-muted-foreground mt-3 text-lg max-w-md">
            {"Premium audiobooks, masterfully narrated, ready whenever you are."}
          </p>
        </div>
      </div>
    </div>
  );
}
